import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { isEmpty } from 'lodash';

import { useTitle, formatDuration, formatPublishedDate, } from "../../../../../config/global";
import "../../../css/setup.css";

import EditInteractiveMediaDialog from '../../../../SharedComponents/DragDrop/EditInteractiveMediaDialog';
import AddContentPopup from '../../../../SharedComponents/AddContentPopup/AddContentPopup';
import AssignPopup from '../../../Components/AssignToDevicePopup';
import DragDrop from '../../../../SharedComponents/DragDrop/DragDrop';
import Layout from '../../../../Layout/Layout'

import { ProgressSpinner } from 'primereact/progressspinner';
import { SplitButton } from 'primereact/splitbutton';
import { Dropdown } from 'primereact/dropdown';
import { Tooltip } from 'primereact/tooltip';
import { Button } from '../../../../../components/ui/Button';

import { useSelector, useDispatch } from 'react-redux';
import { useQueryClient } from 'react-query';
import { setHideTabs, toggleDialog, setCampaignDetails, setLayer } from '../../../../../redux/Slices/Global/globalSlice';
import { useGetCampaign, useGetCampaignLayerContent, usePublishCampaignMutation, usePublishPlaylistMutation, useResetCampaignMutation } from '../../../../../queries';

// layerOptions are defined inside the component to access t()

function InteractiveCampaignSetup() {
  useTitle("Interactive Campaign Setup")
  const { t } = useTranslation();

  const layerOptions = [
    { name: t('layer.main'), code: "MAIN" },
    { name: t('layer.playground'), code: "PLAYGROUND" }
  ];
  const { isPrayerCampaign, campaignDetails, openDialog, layer, assign, isLoading } = useSelector((state) => state.global);
  const dispatch = useDispatch()
  const queryClient = useQueryClient();
  const { id } = useParams();

  const isDefaultPrayer = !JSON.parse(sessionStorage.getItem('is_default_prayer'));

  const { isLoading: campaignLoading, data: campaignInfo } = useGetCampaign({ enabled: true, id });
  const { isLoading: layerLoading, data, refetch } = useGetCampaignLayerContent(id, layer);
  const publishCampaign = usePublishCampaignMutation()
  const resetCampaign = useResetCampaignMutation()
  const publishReferencedPlaylist = usePublishPlaylistMutation()
  const campaignPublishResetLoading = publishCampaign.isLoading || resetCampaign.isLoading || isLoading;

  // Calculate if main layer is empty
  const isMainEmpty = campaignDetails.campaign_duration === 0;

  // Determine if publish button should be disabled
  // ⚠️ has_unpublished_changes, not campaignDetails.status — same fix already
  // applied on the non-interactive CampaignSetup.jsx (status doesn't track
  // real draft/published diff; see CampaignSyncState).
  const disablePublishBtn = useMemo(() => {
    return layerLoading || campaignDetails.has_unpublished_changes !== true || isMainEmpty;
  }, [layerLoading, campaignDetails.has_unpublished_changes, isMainEmpty]);

  // Responsive dropdown actions menu
  const options = [
    {
      label: t('show.show_add_content'),
      icon: 'pi pi-plus',
      command: () => {
        dispatch(toggleDialog("addContent"))
      }
    }, {
      label: t('show.show_assign'),
      icon: 'bx bx-up-arrow-alt',
      command: () => {
        dispatch(toggleDialog("assignDeviceGroup"))
      }
    },
    {
      label: t('buttons.publish'),
      icon: '',
      disabled: disablePublishBtn,
      command: () => {
        publish()
      }
    }, {
      label: t('show.show_reset'),
      visible: campaignDetails.has_unpublished_changes === true && !campaignDetails.neverPublished,
      icon: '',
      command: () => {
        reset()
      }
    }

  ];

  // Get Campaign Details 
  useEffect(() => {
    if (!isEmpty(campaignInfo)) {
      dispatch(setCampaignDetails(campaignInfo));
      dispatch(setLayer("MAIN"))
      dispatch(setHideTabs(true))
    }
  }, [campaignInfo])

  // Publish all campaign content from draft 
  const publish = async () => {
    await publishCampaign.mutateAsync({ id, name: campaignInfo?.name })
  }

  const reset = async () => {
    await resetCampaign.mutateAsync({ id, name: campaignInfo?.name })
  }

  // ⚠️ Same fix as CampaignSetup.jsx: publishing the campaign never
  // republishes a playlist it references, so a dirty referenced playlist
  // needs its own named, actionable control here.
  const publishDirtyPlaylist = async (playlist) => {
    await publishReferencedPlaylist.mutateAsync({ id: playlist.id, name: playlist.name }, {
      onSuccess: () => queryClient.invalidateQueries(['getCampaign', id]),
    })
  }

  const subNavbarProps = useMemo(() => {
    if (!isDefaultPrayer) return null;
    return {
      title: campaignLoading ? t('messages.loading') : campaignDetails?.name || " ",
      date: campaignLoading ? "" : campaignDetails?.has_unpublished_changes === true ? t('show.show_updated_at', { date: formatPublishedDate(campaignDetails?.updated_at) }) : t('show.show_published_at', { date: formatPublishedDate(campaignDetails?.created_at) }) || "",
    };
  }, [campaignDetails]);

  const items = [
    {
      label: <span className="text-[0.85rem]  cursor-auto">
        {campaignDetails.neverPublished ?
          t('show.show_not_yet_published')
          : t('show.show_last_published_on', { date: campaignDetails.last_published ? formatPublishedDate(campaignDetails.last_published) : t('show.show_unknown_date') })}
      </span>,
      disabled: true

    },

    {
      label: <span className="fs-6">{t('show.show_discard_changes')}</span>,
      icon: 'bx bx-undo me-2  ',
      disabled: campaignDetails.has_unpublished_changes !== true || campaignDetails.neverPublished,
      command: reset
    },

  ];

  const subNavbarContent = useMemo(() => {
    if (!isDefaultPrayer) return null;
    return (
      <div className={`flex  justify-end  w-100`}>

        <div className="">
          {campaignPublishResetLoading && (

            <small className='italic mr-3'>{t('show.show_saving')}</small>
          )}
          {Array.isArray(campaignDetails.dirty_playlists) && campaignDetails.dirty_playlists.map((pl) => (
            <div key={pl.id} className="d-flex align-items-center me-2 px-2 rounded" style={{ background: '#fff7e6', border: '1px solid #f5c451' }}>
              <small className="text-nowrap" style={{ color: '#b45309' }}>{t('show.show_referenced_playlist_dirty', { name: pl.name })}</small>
              <Button
                label={t('buttons.publish')}
                className="p-button-sm p-button-text py-1"
                loading={publishReferencedPlaylist.isLoading && publishReferencedPlaylist.variables?.id === pl.id}
                onClick={() => publishDirtyPlaylist(pl)}
              />
            </div>
          ))}
          <Button label=" "
            id="assign-btn"
            data-pr-tooltip={t('show.show_assign_to_device')}
            data-pr-position="bottom"
            icon={<span><i className="bx bx-up-arrow-alt"></i> <i className="bx bx-devices"></i></span>}
            className={`p-button-sm  me-2 p-button-outlined ${isPrayerCampaign ? "d-none" : ""}`}
            onClick={() => dispatch(toggleDialog("assignDeviceGroup"))} disabled={layerLoading} />
          <Tooltip target="#assign-btn" />
          <SplitButton label={isLoading ? "" : t('buttons.publish')}
            icon={`${campaignPublishResetLoading ? "pi pi-spin pi-spinner" : ""}`}
            buttonClassName={`py-2 ${isLoading ? "!w-[117px]" : ""}   ${disablePublishBtn && "!cursor-not-allowed p-disabled"}  ${campaignPublishResetLoading && "!cursor-progress bg-[#E9C08A] border-[#E9C08A]"}`}
            menuButtonClassName={`py-2 !w-[30px] leading-[26.5px]  ${disablePublishBtn ? "bg-[#E9C08A] border-[#E9C08A]" : ""}`} onClick={publish} model={items} />

        </div>
      </div>
    );
  }, [campaignDetails.has_unpublished_changes, campaignDetails.neverPublished, campaignDetails.dirty_playlists, layerLoading, publishCampaign.isLoading, publishReferencedPlaylist.isLoading, publishReferencedPlaylist.variables, assign, isLoading, resetCampaign.isLoading]);

  return (
    <>
      <Layout subNavbarProps={subNavbarProps} subNavbarContent={subNavbarContent}>
        <div className=" mr-1 d-flex justify-content-between mb-3">
          <div className="col-8 col-md-6 d-flex">
            <Dropdown value={layer} options={layerOptions} optionLabel="name" optionValue="code" className='w-50 ' onChange={(e) => dispatch(setLayer(e.value))} />
            {
              // total duration for main layer
              layer == "MAIN" && !layerLoading &&
              <div className='mx-3 d-none d-sm-block'>
                <p className="mb-0 ">{t('show.show_total_duration')}</p>
                <div className='fw-bold'> {formatDuration(campaignDetails.campaign_duration)}</div>
              </div>
            }

          </div>
          {
            !JSON.parse(sessionStorage.getItem('is_default_prayer')) &&
            <>
              <div className='d-none d-md-flex'>
                <Button label={t('show.show_add_content')} icon="pi pi-plus" className='mr-1 py-2  p-button-outlined p-button-secondary' onClick={() => dispatch(toggleDialog("addContent"))} disabled={layerLoading} />
              </div>

              <SplitButton label={t('show.show_option')} model={options} className="p-button-sm mr-2 mb-2 d-flex d-md-none" />
            </>
          }
        </div>
        {
          data?.length == 0 && !layerLoading ?
            <h5 className='w-100 card d-flex align-items-center text-center p-5 mt-5'>
              {t('show.show_no_media_yet')}
            </h5>
            :
            <></>
        }
        {
          layerLoading ?
            <div className='w-100 h-100 d-flex align-items-center' >
              <ProgressSpinner style={{ width: '60px', height: '60px', opacity: .1 }} strokeWidth="3" fill="var(--surface-ground)" animationDuration="1s" />
            </div>
            :
            data?.length != 0 ? <DragDrop data={data} /> : ""
        }

        {openDialog.addContent ? <AddContentPopup /> : <></>}
        {openDialog.assignDeviceGroup ? <AssignPopup campaignId={id} /> : <></>}
        {openDialog.editInteractiveMedia ? <EditInteractiveMediaDialog /> : <></>}

      </Layout>
    </>
  )
}

export default InteractiveCampaignSetup